import React, { useMemo, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { CrossClientQueryAiEvaluationModes, type CrossClientQueryAiEvaluationMode } from '../../../../models/CrossClient'

interface AnalyzeOption {
  key: string
  label: string
  mode: CrossClientQueryAiEvaluationMode
  batchSize: number
  noteCount: number | 'all'
}

const OPTIONS: AnalyzeOption[] = [
  { key: '100', label: '100 clients', mode: CrossClientQueryAiEvaluationModes.STEPPED, batchSize: 100, noteCount: 100 },
  { key: '200', label: '200 clients', mode: CrossClientQueryAiEvaluationModes.STEPPED, batchSize: 200, noteCount: 200 },
  { key: 'all', label: 'Analyze all', mode: CrossClientQueryAiEvaluationModes.ALL, batchSize: 100, noteCount: 'all' },
]

interface CrossClientAnalyzeDropdownProps {
  onStart: (mode: CrossClientQueryAiEvaluationMode, batchSize: number) => void | Promise<void>
  remaining: number
  disabled?: boolean
  loading?: boolean
}

export const CrossClientAnalyzeDropdown: React.FC<CrossClientAnalyzeDropdownProps> = ({ onStart, remaining, disabled, loading }) => {
  const [selectedKey, setSelectedKey] = useState<string>(OPTIONS[0].key)
  const [open, setOpen] = useState(false)

  const availableOptions = useMemo(
    () => OPTIONS.filter((option) => option.mode === CrossClientQueryAiEvaluationModes.ALL || remaining > option.batchSize),
    [remaining],
  )

  const selected = availableOptions.find((o) => o.key === selectedKey) ?? availableOptions[0]

  const noteText =
    selected.noteCount === 'all'
      ? "I'll analyze all unconfirmed clients and send you a notification when done."
      : `I'll stop after analyzing ${selected.noteCount} clients and send you a notification.`

  const handleStart = () => {
    setOpen(false)
    void onStart(selected.mode, selected.batchSize)
  }

  return (
    <Dropdown show={open} onToggle={(nextOpen) => setOpen(nextOpen)} autoClose='outside' align='end'>
      <Dropdown.Toggle as={Button} variant='header' className='button-medium cross-client-pill' disabled={disabled || loading}>
        Analyze <i className='bi bi-chevron-expand' />
      </Dropdown.Toggle>
      <Dropdown.Menu className='cross-client-popover cross-client-analyze-popover p-3'>
        <div className='d-flex flex-column gap-1'>
          <div className='paragraph-medium medium'>Stay in control of costs and results.</div>
          <div className='paragraph-small zocks-text-gray-700'>How many clients at a time?</div>
        </div>
        <div className='d-flex flex-column gap-2'>
          {availableOptions.map((option) => (
            <label
              key={option.key}
              className={classNames('d-flex align-items-center gap-2 px-3 py-2 cross-client-analyze-option', {
                'cross-client-analyze-option--selected': option.key === selected.key,
              })}
            >
              <input
                type='radio'
                name='cross-client-analyze-mode'
                value={option.key}
                checked={option.key === selected.key}
                onChange={() => setSelectedKey(option.key)}
              />
              <span className='paragraph-medium'>{option.label}</span>
            </label>
          ))}
        </div>
        <div className='paragraph-small zocks-text-gray-700'>{noteText}</div>
        <Button variant='primary' className='button-medium w-100 cross-client-analyze-start' onClick={handleStart} loading={loading} disabled={disabled}>
          Start
        </Button>
      </Dropdown.Menu>
    </Dropdown>
  )
}
